import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { colors, radii } from '../theme/tokens';
import Icon from './ui/Icon';

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!token) return;
    fetchLeaderboard();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const authConfig = { headers: { Authorization: `Bearer ${token}` } };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leaderboard?limit=10`, authConfig);
      setLeaderboard(response.data);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leaderboard/stats`, authConfig);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const getMedalIcon = (rank) => {
    if (rank <= 3) return <Icon name="emoji_events" size={24} color="#fff" />;
    return `#${rank}`;
  };

  const getTierColor = (tier) => {
    if (tier === 'platinum') return '#E5E4E2';
    if (tier === 'gold') return '#FFD700';
    if (tier === 'silver') return '#C0C0C0';
    return '#91C83E';
  };

  const getTierIcon = (tier) => {
    if (tier === 'platinum') return 'diamond';
    if (tier === 'gold') return 'star';
    if (tier === 'silver') return 'auto_awesome';
    return 'auto_awesome';
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading leaderboard...</div>
      </div>
    );
  }

  if (leaderboard.length === 0) {
    return (
      <div style={styles.container}>
        <h2 style={styles.title}><Icon name="emoji_events" size={30} color={colors.parGreenBright} /> Top Contributors</h2>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}><Icon name="rocket_launch" size={56} color={colors.parGreenBright} /></div>
          <p>Be the first to contribute!</p>
          <p style={styles.emptyHint}>Start adding car parts to see your name here</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}><Icon name="emoji_events" size={30} color={colors.parGreenBright} /> Top Contributors Leaderboard</h2>
        <p style={styles.subtitle}>
          Celebrating our amazing community members who help build the database!
        </p>
      </div>

      {stats && (
        <div style={styles.statsBar}>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{stats.totalApprovedParts}</div>
            <div style={styles.statLabel}>Total Parts Added</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{stats.totalContributors}</div>
            <div style={styles.statLabel}>Active Contributors</div>
          </div>
          {stats.topContributor && (
            <div style={styles.statBox}>
              <div style={styles.statValue}>{stats.topContributor.count}</div>
              <div style={styles.statLabel}>Top Record by {stats.topContributor.name}</div>
            </div>
          )}
        </div>
      )}

      <div style={styles.leaderboardGrid}>
        {leaderboard.map((user, index) => {
          const isTopThree = user.rank <= 3;
          const cardStyle = isTopThree 
            ? { ...styles.card, ...styles.topThreeCard, borderColor: user.rank === 1 ? '#FFD700' : user.rank === 2 ? '#C0C0C0' : '#CD7F32' }
            : styles.card;

          return (
            <div 
              key={user._id} 
              style={cardStyle}
              className="leaderboard-card"
            >
              <div style={styles.rankBadge}>
                <span style={styles.rankText}>{getMedalIcon(user.rank)}</span>
              </div>

              <div style={styles.cardContent}>
                <div style={styles.userInfo}>
                  <div style={styles.nameRow}>
                    <span style={styles.userName}>{user.name}</span>
                    <span 
                      style={{
                        ...styles.tierBadge, 
                        background: getTierColor(user.tier),
                        color: user.tier === 'platinum' || user.tier === 'silver' ? '#1E1E1E' : 'white'
                      }}
                    >
                      <Icon name={getTierIcon(user.tier)} size={12} color={user.tier === 'platinum' || user.tier === 'silver' ? '#1E1E1E' : 'white'} /> {user.tier.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div style={styles.statsRow}>
                  <div style={styles.stat}>
                    <div style={styles.statIconBox}>
                      <Icon name="check_circle" size={20} color={colors.parGreenBright} />
                    </div>
                    <div>
                      <div style={styles.statNumber}>{user.approvedSubmissionsCount}</div>
                      <div style={styles.statText}>Parts Added</div>
                    </div>
                  </div>

                  <div style={styles.stat}>
                    <div style={styles.statIconBox}>
                      <Icon name="paid" size={20} color={colors.parGreenBright} />
                    </div>
                    <div>
                      <div style={styles.statNumber}>{user.tokens}</div>
                      <div style={styles.statText}>Tokens</div>
                    </div>
                  </div>
                </div>

                {isTopThree && (
                  <div style={styles.topBanner}>
                    TOP {user.rank === 1 ? 'CHAMPION' : user.rank === 2 ? 'RUNNER-UP' : 'ACHIEVER'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.motivationalBox}>
        <div style={styles.motivationalIcon}><Icon name="rocket_launch" size={44} color="#fff" /></div>
        <h3 style={styles.motivationalTitle}>Want to Join the Leaderboard?</h3>
        <p style={styles.motivationalText}>
          Start contributing! Add car parts, fulfill requests, and earn your spot among the top contributors.
          Every approved submission counts towards your ranking!
        </p>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        .leaderboard-card {
          animation: slideIn 0.5s ease-out;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .leaderboard-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(47, 182, 92, 0.3) !important;
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px'
  },
  loadingText: {
    textAlign: 'center',
    padding: '50px',
    fontSize: '18px',
    color: colors.textMuted
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px'
  },
  title: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    color: colors.text,
    fontSize: '30px',
    marginBottom: '10px',
    fontWeight: 'bold'
  },
  subtitle: {
    color: colors.textMuted,
    fontSize: '16px',
    marginTop: '0'
  },
  statsBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  },
  statBox: {
    background: 'linear-gradient(135deg, #2fb65c 0%, #91C83E 100%)',
    padding: '20px',
    borderRadius: '15px',
    textAlign: 'center',
    color: 'white',
    boxShadow: '0 4px 15px rgba(47, 182, 92, 0.3)'
  },
  statValue: {
    fontSize: '36px',
    fontWeight: 'bold',
    marginBottom: '5px'
  },
  statLabel: {
    fontSize: '14px',
    opacity: 0.9
  },
  leaderboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  },
  card: {
    background: colors.glass,
    borderRadius: radii.lg,
    padding: '20px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    position: 'relative',
    overflow: 'hidden',
    border: `1px solid ${colors.glassBorder}`,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    color: colors.text
  },
  topThreeCard: {
    background: colors.glassStrong,
    boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
    border: '2px solid'
  },
  rankBadge: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2fb65c 0%, #91C83E 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'white',
    boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
  },
  rankText: {
    fontSize: '24px'
  },
  cardContent: {
    marginTop: '10px'
  },
  userInfo: {
    marginBottom: '20px'
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
    flexWrap: 'wrap'
  },
  userName: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: colors.text
  },
  tierBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px'
  },
  email: {
    fontSize: '14px',
    color: colors.textMuted,
    wordBreak: 'break-all'
  },
  statsRow: {
    display: 'flex',
    justifyContent: 'space-around',
    gap: '20px',
    padding: '15px 0'
  },
  stat: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  statIconBox: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'rgba(47,182,92,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statNumber: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: colors.parGreenBright
  },
  statText: {
    fontSize: '12px',
    color: colors.textMuted
  },
  topBanner: {
    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
    color: 'white',
    padding: '10px',
    textAlign: 'center',
    borderRadius: '8px',
    fontWeight: 'bold',
    fontSize: '14px',
    marginTop: '15px',
    boxShadow: '0 2px 10px rgba(255, 215, 0, 0.4)'
  },
  motivationalBox: {
    background: 'linear-gradient(135deg, #91C83E 0%, #2fb65c 100%)',
    padding: '30px',
    borderRadius: '15px',
    textAlign: 'center',
    color: 'white',
    boxShadow: '0 6px 20px rgba(47, 182, 92, 0.3)'
  },
  motivationalIcon: {
    fontSize: '48px',
    marginBottom: '15px'
  },
  motivationalTitle: {
    fontSize: '24px',
    marginBottom: '10px',
    fontWeight: 'bold'
  },
  motivationalText: {
    fontSize: '16px',
    opacity: 0.95,
    maxWidth: '700px',
    margin: '0 auto'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.lg,
    color: colors.text,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)'
  },
  emptyIcon: {
    marginBottom: '20px'
  },
  emptyHint: {
    color: colors.textFaint,
    fontSize: '14px'
  }
};

export default Leaderboard;
